/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Button,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import React from 'react';
import { CustomIconButton } from '@opengeoweb/shared';
import { Delete, DrawMask } from '@opengeoweb/theme';
import type { DrawingListItem } from '../../store/warningsDrawings/types';
import { useWarningsTranslation } from '../../utils/i18n';

export interface DrawingToolFormMasksProps {
  masks: DrawingListItem[];
  selectedMaskId?: string;
  onSelectMask: (maskId?: string) => void;
  onHoverMask: (maskId?: string) => void;
  isDisabled?: boolean;
  isLoadingMasks?: boolean;
}

export const DrawingToolFormMasks: React.FC<DrawingToolFormMasksProps> = ({
  masks,
  selectedMaskId,
  onSelectMask,
  onHoverMask,
  isDisabled = false,
  isLoadingMasks = false,
}: DrawingToolFormMasksProps) => {
  const { t } = useWarningsTranslation();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const isOpen = Boolean(anchorEl);

  const selectedMask = masks.find((mask) => mask.id === selectedMaskId);

  const closeMenu = (): void => {
    setAnchorEl(null);
    onHoverMask(undefined);
  };

  const hoverMask = (maskId?: string): void => {
    if (isOpen) {
      onHoverMask(maskId);
    }
  };

  return (
    <>
      <CustomIconButton
        data-testid="drawingToolForm-masks-button"
        disabled={isDisabled}
        isSelected={isOpen || !!selectedMask}
        onClick={(event): void => setAnchorEl(event.currentTarget)}
        size="medium"
        tooltipTitle={
          selectedMask
            ? `${t('drawing-tool-form-masks')}: ${selectedMask.objectName}`
            : t('drawing-tool-form-masks-tooltip')
        }
        variant="tool"
      >
        <DrawMask />
      </CustomIconButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        disableAutoFocusItem
        onClose={closeMenu}
        open={isOpen}
        slotProps={{
          backdrop: { style: { opacity: 0, transition: 'none' } },
          paper: { sx: { minWidth: 200 } },
          transition: { onExited: (): void => onHoverMask(undefined) },
        }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <ListSubheader
          data-testid="drawingToolForm-masks-title"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            ' .MuiSvgIcon-root': {
              mr: 0,
            },
          }}
        >
          <Typography sx={{ fontSize: '12px' }}>
            {t('drawing-tool-form-masks')}
          </Typography>
          {selectedMask ? (
            <Button
              data-testid="drawingToolForm-masks-clear"
              endIcon={<Delete />}
              onClick={(): void => {
                onSelectMask(undefined);
                closeMenu();
              }}
              size="small"
              sx={{ minWidth: 0, mr: -1 }}
              variant="flat"
            >
              {t('drawing-tool-form-masks-clear')}
            </Button>
          ) : null}
        </ListSubheader>
        {isLoadingMasks ? (
          <Typography
            data-testid="drawingToolForm-masks-loading"
            sx={{ padding: 1, fontSize: 14 }}
          >
            {t('loading')}
          </Typography>
        ) : null}
        {!isLoadingMasks && masks.length === 0 ? (
          <Typography
            data-testid="drawingToolForm-masks-empty"
            sx={{ padding: 1, fontSize: 14, maxWidth: 260 }}
          >
            {t('drawing-tool-form-masks-empty')}
          </Typography>
        ) : null}
        {masks.map((mask) => (
          <MenuItem
            key={mask.id}
            onClick={(): void => {
              onSelectMask(mask.id === selectedMaskId ? undefined : mask.id);
              closeMenu();
            }}
            onMouseEnter={(): void => hoverMask(mask.id)}
            onMouseLeave={(): void => hoverMask(undefined)}
            selected={mask.id === selectedMaskId}
          >
            {mask.objectName}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
